import { action } from '@ember/object';
import Service, { type Registry as Services, service } from '@ember/service';
import { task, type TaskForAsyncTaskFunction } from 'ember-concurrency';
import {
  formatUrl,
  type FormatUrlParams,
} from 'frontend-utils/utils/url/format-url';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type { AppliedMappingFilterIds } from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-filter';
import type HotelSettingsCentralMappingHierarchyController from 'lumen/controllers/hotel/settings/central-mapping/hierarchy';
import type HotelSettingsCentralMappingMappingController from 'lumen/controllers/hotel/settings/central-mapping/mapping';
import type MarketSettingsCentralMappingHierarchyController from 'lumen/controllers/market/settings/central-mapping/hierarchy';
import type MarketSettingsCentralMappingMappingController from 'lumen/controllers/market/settings/central-mapping/mapping';
import type {
  CentralMappingBreakdown,
  CentralMappingCategory,
  CentralMappingMarketSubscription,
} from 'lumen/models';
import type {
  MappingEntriesRawResponseMeta,
  MappingEntriesResponseMeta,
} from 'lumen/serializers/central-mapping/mapping-entry';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import {
  type Breakdown,
  BREAKDOWNS,
  BREAKDOWNS_WITHOUT_FILTER_REQUIREMENT,
  CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS,
  type CentralMappingFilterQueryParams,
  type CentralMappingMappingEntryExtendedModel,
  HIDDEN_PARENT_CATEGORY_ID,
  LARGE_MARKET_PROPERTY_LIMIT,
  PMS_VALUE_TYPES,
  type PmsValueType,
} from 'lumen/utils/central-mapping-constants';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export type CentralMappingExtendedController =
  | HotelSettingsCentralMappingHierarchyController
  | HotelSettingsCentralMappingMappingController
  | MarketSettingsCentralMappingHierarchyController
  | MarketSettingsCentralMappingMappingController;

const controllerIsForMappingRoute = (
  controller: CentralMappingExtendedController,
): controller is HotelSettingsCentralMappingMappingController => {
  return 'handleUnsavedChanges' in controller;
};

export default class CentralMappingService extends Service {
  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  getBreakdownsTask = task({ restartable: true }, async () => {
    try {
      const breakdowns = await this.store.query<CentralMappingBreakdown>(
        'central-mapping/breakdown',
        {
          namespace: APIGATEWAY,
          typeKey: 'mapping',
          [this.hotelOrMarketKey]: this.hotelAndMarketService.hotelOrMarket.id,
        },
      );

      return breakdowns;
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  getHierarchyTask = task(
    { restartable: true },
    async (breakdown: Breakdown, managedBy?: string) => {
      let hierarchyResponse = null;
      const params: Record<string, string | string[] | number | null> = {
        namespace: APIGATEWAY,
        typeKey: 'mapping/categories',
        [this.hotelOrMarketKey]: this.hotelAndMarketService.hotelOrMarket.id,
        breakdown,
      };

      if (managedBy) {
        params['managed_by'] = managedBy;
      }

      try {
        hierarchyResponse = await this.store.query<CentralMappingCategory>(
          'central-mapping/category',
          params,
        );

        let hierarchy = [...hierarchyResponse];

        const isNonFixedRootHierarchy = hierarchy.every((category) => {
          return !category.isFixed;
        });

        if (isNonFixedRootHierarchy) {
          // If the hierarchy does not have fixed root categories,
          // we need to create a hidden parent category to act as a parent to group all the non-fixed categories.
          // The hidden parent category will not be shown in the UI.
          let rootCategory = this.store.peekRecord<CentralMappingCategory>(
            'central-mapping/category',
            `${HIDDEN_PARENT_CATEGORY_ID}-${breakdown}`,
          );

          if (!rootCategory) {
            rootCategory = this.store.createRecord<CentralMappingCategory>(
              'central-mapping/category',
              {
                id: `${HIDDEN_PARENT_CATEGORY_ID}-${breakdown}`,
                name: '',
                isFixed: false,
                parent: null,
                children: [],
                // @ts-expect-error: Incorrect type
                valueType: breakdown,
                isHiddenParent: true,
              },
            );
          }

          hierarchy.forEach((category) => {
            if (!category.parent) {
              category.parent = rootCategory;

              // This is needed to get rid of the hasDirtyRelationships triggered by the parent assignment.
              // The parent assignment will not be persisted to the BE, so we need to update the relationships here.
              category.updateRelationships();
            }
            return category;
          });

          hierarchy = [...hierarchy, rootCategory];
        }

        return {
          hierarchy,
        };
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  getMappingEntriesTask = task(
    { restartable: true },
    async ({
      breakdown,
      valueType,
      pageSize,
      page,
      filterOptions = {},
      managedBy,
      includeArchived,
    }: {
      breakdown: Breakdown;
      filterOptions?: CentralMappingFilterQueryParams;
      includeArchived?: boolean;
      managedBy?: string;
      page?: number;
      pageSize?: number;
      valueType: PmsValueType;
    }) => {
      try {
        // First, fetch the hierarchy, so Ember Data can link hierarchy categories in the store to the mapping entries automatically.
        await this.getHierarchyTask.perform(breakdown, managedBy);

        // If this is a market, fetch the market subscription to link the market info to the mapping entries.
        if (this.hotelAndMarketService.market) {
          const marketSubscriptions =
            await this.getMarketSubscriptionTask.perform();
          /*
           * If there are more than LARGE_MARKET_PROPERTY_LIMIT properties in the market, and there are no property filters active,
           * and the breakdown requires initial filters to be applied (i.e. all except DAY_OF_WEEK and GUEST_COUNTRY),
           * then do not fetch the mapping entries and return null instead.
           * This is to prevent performance issues when loading the mapping page for large markets.
           */
          if (
            !BREAKDOWNS_WITHOUT_FILTER_REQUIREMENT.includes(breakdown) &&
            !this.hasActivePropertyFilters(filterOptions) &&
            marketSubscriptions.length > LARGE_MARKET_PROPERTY_LIMIT
          ) {
            return null;
          }
        }

        const mappingEntryModelName =
          this.getMappingEntryModelNameByBreakdown(breakdown);

        // Clear the mapping entries from the store before fetching them.
        // Users might arrive at the mapping page while automapping is in progress. In that case, the first fetch will
        // have the old state of the mapping entries. After automapping is complete, we fetch the same mapping entries again,
        // but the new state might show as unsaved changes (dirty attributes) instead.
        this.store.unloadAll(mappingEntryModelName);

        const params: Record<string, unknown> = {
          namespace: APIGATEWAY,
          typeKey: 'mapping/mapping_entries',
          [this.hotelOrMarketKey]: this.hotelAndMarketService.hotelOrMarket.id,
          breakdown,
          value_type: valueType,
        };
        if (page) {
          params['page'] = page;
        }
        if (pageSize) {
          params['page_size'] = pageSize;
        }
        if (managedBy) {
          params['managed_by'] = managedBy;
        }
        if (includeArchived) {
          params['include_archived'] = includeArchived;
        }

        const {
          chains,
          brands,
          properties,
          view,
          orderBy,
          search,
          barBased,
          categories,
        } = filterOptions;
        if (orderBy) {
          params['order_by'] = orderBy;
        }
        if (view) {
          params['view'] = view;
        }
        if (search) {
          params['search'] = search;
        }
        if (chains?.length) {
          params['chains'] = chains.split(',');
        }
        if (brands?.length) {
          params['brands'] = brands.split(',');
        }
        if (properties?.length) {
          params['subscriptions'] = properties.split(',');
        }
        if (barBased) {
          params['bar_based_view'] = barBased;
        }
        if (categories?.length) {
          params['categories'] = categories.split(',');
        }

        const mappingEntriesResponse =
          await this.store.query<CentralMappingMappingEntryExtendedModel>(
            // @ts-expect-error: Incorrect type
            mappingEntryModelName,
            params,
          );

        const meta =
          mappingEntriesResponse.meta as unknown as MappingEntriesResponseMeta;

        return {
          mappingEntries: Array.from(mappingEntriesResponse),
          savingStatus: meta.status,
          paginationMeta: meta.paginationMeta,
        };
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        return null;
      }
    },
  );

  getMarketSubscriptionTask = task({ restartable: true }, async () => {
    try {
      const marketId = this.hotelAndMarketService.hotelOrMarket.id;

      if (!marketId) {
        throw new Error(
          'Market ID is not available in getMarketSubscriptionTask',
        );
      }

      let marketSubscriptions = this.store
        .peekAll<CentralMappingMarketSubscription>(
          'central-mapping/market-subscription',
        )
        .filter((subscription) => {
          return subscription.marketId === marketId;
        });

      if (!marketSubscriptions.length) {
        // Clear any previously cached market subscriptions to ensure we fetch the latest data.
        this.store.unloadAll('central-mapping/market-subscription');

        marketSubscriptions =
          await this.store.query<CentralMappingMarketSubscription>(
            'central-mapping/market-subscription',
            {
              namespace: APIGATEWAY,
              typeKey: 'mapping/market_info',
              market_id: marketId,
            },
          );

        // Assign the market relationship to each fetched marketSubscription to enable filtering cached marketSubscriptions by market ID.
        marketSubscriptions.forEach((marketSubscription) => {
          marketSubscription.marketId = marketId;
        });
      }

      return marketSubscriptions;
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  showRetryAlertTask = task(
    { restartable: true },
    async (
      taskCallback: TaskForAsyncTaskFunction<
        {
          restartable: boolean;
        },
        () => Promise<{
          isBeingProcessed: boolean;
        } | null | void>
      >,
    ): Promise<void> => {
      const confirmWasClicked = await this.alerting.showErrorAlert({
        title: this.intl.t('settings.central_mapping.hierarchy.alert.title'),
        text: this.intl.t('settings.central_mapping.hierarchy.alert.text'),
        buttons: {
          cancel: {
            text: this.intl.t(
              'settings.central_mapping.hierarchy.alert.cancel',
            ),
          },
          confirm: {
            text: this.intl.t(
              'settings.central_mapping.hierarchy.alert.confirm',
            ),
          },
        },
      });

      if (confirmWasClicked) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        taskCallback.perform();
      }
    },
  );

  updateMappingTask = task(
    { restartable: true },
    async (
      mappingEntries: CentralMappingMappingEntryExtendedModel[],
      breakdown: Breakdown,
      valueType: PmsValueType,
      managedBy?: string,
    ) => {
      const serializedMappingEntries = mappingEntries.map((mappingEntry) =>
        mappingEntry.serialize({ includeId: true }),
      );

      const payload = {
        meta: { breakdown },
        data: serializedMappingEntries,
      };

      const params: Record<string, string> = {
        [this.hotelOrMarketKey]: this.hotelAndMarketService.hotelOrMarket.id!,
        breakdown,
        value_type: valueType,
      };
      if (managedBy !== undefined) {
        params['managed_by'] = managedBy;
      }

      const url = formatUrl(
        '/apigateway/v1/app/mapping/mapping_entries',
        params,
        this.ajax.host,
      );

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const response = await this.ajax.post(url, {
        data: payload,
      });

      return response as unknown as {
        data: Record<string, unknown>[];
        meta: MappingEntriesRawResponseMeta;
      };
    },
  );

  get breakdowns(): CentralMappingBreakdown[] {
    if (!this.getBreakdownsTask.last?.value) {
      return [];
    }

    return this.getBreakdownsTask.last.value.slice();
  }

  /**
   * Determines if the user has access to the central mapping settings.
   * Access is granted if the user has the central mapping feature flag enabled
   * or has product access (Pricing Assistant, Rates, Parity, or Benchmark Insight mapping tab).
   */
  get hasCentralMappingHotelAccess(): boolean {
    const hasProductAccess =
      this.access.hasPricingAssistantTab ||
      this.access.hasRates ||
      this.access.hasParity ||
      this.access.hasBenchmarkInsightMappingTab;

    return this.access.hasCentralMapping || hasProductAccess;
  }

  get hotelOrMarketKey(): string {
    return this.hotelAndMarketService.hotel ? 'subscription_id' : 'market_id';
  }

  get sortedBreakdowns(): CentralMappingBreakdown[] {
    const breakdownValues = Object.values(BREAKDOWNS);

    return this.breakdowns
      .filter((breakdown) => {
        return breakdown.available;
      })
      .sort((a, b) => {
        return (
          breakdownValues.indexOf(a.breakdown) -
          breakdownValues.indexOf(b.breakdown)
        );
      });
  }

  get sortedBreakdownsBenchmark(): CentralMappingBreakdown[] {
    return this.sortedBreakdowns.filter((breakdown) => {
      return breakdown.breakdown === BREAKDOWNS.BENCHMARK_MARKET_SEGMENT;
    });
  }

  get sortedBreakdownsBi(): CentralMappingBreakdown[] {
    return this.sortedBreakdowns.filter((breakdown) => {
      return breakdown.breakdown !== BREAKDOWNS.BENCHMARK_MARKET_SEGMENT;
    });
  }

  getMappingEntryModelNameByBreakdown(breakdown: Breakdown): string {
    switch (breakdown) {
      case BREAKDOWNS.MARKET_SEGMENT:
      case BREAKDOWNS.RATE_CODE:
        return 'central-mapping/mapping-entry-market-segment';
      case BREAKDOWNS.ROOM_TYPE:
        return 'central-mapping/mapping-entry-room-type';
      case BREAKDOWNS.DAY_OF_WEEK:
        return 'central-mapping/mapping-entry-day-of-week';
      default:
        // These breakdowns (TRAVEL_AGENT, GUEST_COUNTRY, COMPANY, SOURCE, ORIGIN) are handled by the default mapping entry model.
        return 'central-mapping/mapping-entry';
    }
  }

  hasActivePropertyFilters(
    filterOptions: CentralMappingFilterQueryParams,
  ): boolean {
    const { properties = '', brands = '', chains = '' } = filterOptions;
    return brands?.length > 0 || chains?.length > 0 || properties?.length > 0;
  }

  async uploadMappingEntries(
    file: File,
    breakdown: Breakdown,
    valueType: PmsValueType,
  ): Promise<void> {
    const uploadURL = new URL(
      '/apigateway/v1/app/mapping/mapping_entries/upload/',
      this.ajax.host,
    );

    const params = new URLSearchParams({
      [this.hotelOrMarketKey]:
        this.hotelAndMarketService.hotelOrMarket.id ?? '',
      breakdown,
      value_type: valueType,
    });

    uploadURL.search = params.toString();

    const formData = new FormData();
    formData.append('file', file);

    return this.ajax.request(uploadURL.toString(), {
      method: 'POST',
      data: formData,
      contentType: false,
      processData: false,
      mimeType: 'multipart/form-data',
    });
  }

  @action exportMappingData(
    breakdown: Breakdown,
    valueType: PmsValueType,
    appliedFilters: AppliedMappingFilterIds,
  ): void {
    const queryParams: FormatUrlParams = {
      [this.hotelOrMarketKey]:
        this.hotelAndMarketService.hotelOrMarket.id ?? '',
      breakdown,
      value_type: valueType,
    };

    const {
      barBasedId,
      brandIds,
      categoryIds,
      chainIds,
      propertyIds,
      search,
      sortDirectionId,
      viewId,
      includeArchivedId,
    } = appliedFilters;

    if (sortDirectionId) {
      queryParams['order_by'] = `value_${sortDirectionId}`;
    }
    if (includeArchivedId) {
      queryParams['include_archived'] =
        includeArchivedId === CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.SHOW;
    }
    if (viewId) {
      queryParams['view'] = viewId;
    }
    if (search) {
      queryParams['search'] = search;
    }
    if (barBasedId) {
      queryParams['bar_based_view'] = barBasedId;
    }
    let url = formatUrl(
      `/apigateway/v1/app/download/central_mapping/mapping/mapping_entries`,
      queryParams,
      this.ajax.host,
    );
    if (chainIds?.length) {
      chainIds.forEach((chainId) => {
        url = url.concat(`&chains[]=${chainId}`);
      });
    }
    if (brandIds?.length) {
      brandIds.forEach((brandId) => {
        url = url.concat(`&brands[]=${brandId}`);
      });
    }
    if (propertyIds?.length) {
      propertyIds.forEach((propertyId) => {
        url = url.concat(`&subscriptions[]=${propertyId}`);
      });
    }
    if (categoryIds?.length) {
      categoryIds.forEach((categoryId) => {
        url = url.concat(`&categories[]=${categoryId}`);
      });
    }

    openUrl(url);
  }

  @action
  async onRouteWillChange(
    routeName: string,
    controller: CentralMappingExtendedController,
    transition: Transition,
  ): Promise<void> {
    // Even after saving, hasChanges may remain true while the updateMappingTask is still running.
    // This happens because the task hasn't completed and the controller state isn't updated yet.
    // Therefore, avoid showing the alert if the task is in progress by checking if the task is running.
    if (
      transition.to?.name !== routeName &&
      controller.hasChanges &&
      !this.updateMappingTask.isRunning
    ) {
      transition.abort();

      const canProceedWithAction = controllerIsForMappingRoute(controller)
        ? await controller.handleUnsavedChanges()
        : await this.showUnsavedChangesAlert(controller.discardChanges);

      if (!canProceedWithAction) {
        return;
      }

      transition.retry();
    }
  }

  @action
  async showMarketSegmentationUnsavedChangesAlert(
    saveChangesAndAutomap: () => void,
    valueType: PmsValueType,
  ): Promise<boolean> {
    const isMarketCode = valueType === PMS_VALUE_TYPES.MARKET_CODE;

    const confirmationText = isMarketCode
      ? this.intl.t(
          'central_mapping.confirmation_modal.market-segmentation.action.primary',
        )
      : this.intl.t('general.save_and_continue');

    const confirmationButtonClassName = isMarketCode
      ? 'prism-button prism-button--intelligence'
      : '';

    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      title: this.intl.t('central_mapping.confirmation_modal.title'),
      text: this.intl.t(
        'central_mapping.confirmation_modal.market-segmentation.description',
      ),
      dangerMode: false,
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: confirmationText,
          className: confirmationButtonClassName,
        },
      },
    });

    if (!confirmWasClicked) {
      return false;
    }

    saveChangesAndAutomap();

    return true;
  }

  @action
  async showUnsavedChangesAlert(discardChanges: () => void): Promise<boolean> {
    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      title: this.intl.t('central_mapping.confirmation_modal.title'),
      text: this.intl.t('central_mapping.confirmation_modal.description'),
      dangerMode: true,
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t(
            'central_mapping.confirmation_modal.action.primary',
          ),
        },
      },
    });

    if (!confirmWasClicked) {
      return false;
    }

    discardChanges();

    return true;
  }

  @action
  trackMappingEntryChanged({
    mappingChange,
    rowCount,
    rowType,
  }: {
    mappingChange: string;
    rowCount?: number;
    rowType?: 'parent' | 'child' | 'bulk';
  }): void {
    const props = {
      eventTrigger: 'change-mapping',
      mappingChange,
      rowCount: rowCount ?? 1,
      rowType: rowType ?? 'child',
    };

    this.usageAnalytics.track('Mapping Entry Changed', props);
  }

  @action
  async waitForUpdateMappingTask(): Promise<boolean> {
    if (this.updateMappingTask.isRunning) {
      await this.updateMappingTask.last;
    }
    return this.updateMappingTask.last?.isSuccessful ?? false;
  }
}

declare module '@ember/service' {
  interface Registry {
    'central-mapping': CentralMappingService;
  }
}
